import {fieldContentGenerators} from "@/components/call-list/CallListPage";
import {RecordList} from "@/components/RecordList";
import {useHookClass} from "@dvrt/graphql-widgets";
import {useState, useEffect} from "react";
import DataSource, {Call} from "./DataSource";
import {Dialog, DialogContent, IconButton, TextField} from "@mui/material";
import {FormattedDialogTitle} from "@dvrt/graphql-widgets";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {useIntl, defineMessage} from "react-intl";
import {useDebounced} from "@/hooks/useDebounced";

type Props = {
    ivrId: number;
    domainId: number;
};

/**
 * Call list for auto attendants.
 * @param ivrId ID of the auto attendant to show calls for.
 * @param domainId ID of the domain the auto attendant belongs to.
 **/
const IvrCalls = ({ivrId, domainId}: Props) => {
    const intl = useIntl();

    const [open, setOpen] = useState(false);
    const [showDetailOf, setShowDetailOf] = useState<Call | null>(null);

    const [detailRelatedCalls, setDetailRelatedCalls] = useState<Call[]>([]);
    const [fetchingRelatedCalls, setFetchingRelatedCalls] = useState<boolean>(false);
    const [filterValue, setFilterValue] = useState<string>("");

    // Debounce the filter value with 400ms delay
    const debouncedFilterValue = useDebounced(filterValue, 400);

    const dataSource = useHookClass(DataSource, ivrId, debouncedFilterValue);

    useEffect(() => {
        if (showDetailOf) {
            const fetch = async () => {
                setFetchingRelatedCalls(true);
                const calls = await dataSource.fetchRelatedCalls(showDetailOf, domainId);
                setDetailRelatedCalls(calls);
                setFetchingRelatedCalls(false);
            };

            fetch();
        }
    }, [dataSource, showDetailOf, domainId]);

    const detailRenderer = (call: Call) => (
        <div style={{display: "flex", alignItems: "center"}}>
            <IconButton
                onClick={() => {
                    setShowDetailOf(call);
                    setOpen(true);
                }}
                color="primary"
                size="large"
            >
                <FontAwesomeIcon icon={"info-circle"} />
            </IconButton>
        </div>
    );

    return (
        <div>
            <Dialog open={open}>
                <FormattedDialogTitle
                    title={defineMessage({id: "call_list.detail", defaultMessage: "Detail"})}
                    closingButton
                    onClose={() => setOpen(false)}
                />
                <DialogContent>
                    <RecordList<Call>
                        records={detailRelatedCalls}
                        loading={showDetailOf === null || fetchingRelatedCalls}
                        style={{marginTop: 10}}
                        initialOrder={["connectTime"]}
                        fields={[
                            {
                                name: "remoteNumber",
                                label: defineMessage({id: "call_list.caller", defaultMessage: "Caller"}),
                            },
                            {
                                name: "localNumber",
                                label: defineMessage({id: "call_list.callee", defaultMessage: "Callee"}),
                            },
                            {
                                name: "connectTime",
                                label: defineMessage({id: "call_list.connectTime", defaultMessage: "Connect time"}),
                            },
                            {
                                name: "disconnectTime",
                                label: defineMessage({
                                    id: "call_list.disconnectTime",
                                    defaultMessage: "Disconnect time",
                                }),
                            },
                        ]}
                        generators={fieldContentGenerators as Record<keyof Call, (call: Call) => any>}
                    />
                </DialogContent>
            </Dialog>
            <div style={{display: "flex", alignItems: "baseline"}}>
                <TextField
                    variant="standard"
                    placeholder={intl.formatMessage({
                        id: "call_list.filterPlaceholder",
                        defaultMessage: "Local or remote number",
                    })}
                    style={{flexGrow: 1}}
                    value={filterValue}
                    onChange={(e) => setFilterValue(e.target.value)}
                />
            </div>
            <RecordList
                records={dataSource.calls}
                loading={dataSource.loading}
                style={{marginTop: 10}}
                initialOrder={["~callTime"]}
                fields={[
                    {
                        name: "remoteNumber",
                        label: defineMessage({id: "call_list.remoteNumber", defaultMessage: "Remote number"}),
                    },
                    {
                        name: "callTime",
                        label: defineMessage({id: "call_list.callTime", defaultMessage: "Time"}),
                    },
                    {
                        name: "recordingId",
                        label: defineMessage({id: "call_list.recording", defaultMessage: "Recording"}),
                        sortable: false,
                    },
                ]}
                generators={fieldContentGenerators as Record<keyof Call, (call: Call) => any>}
                actions={[
                    {
                        header: {
                            name: "detail",
                            label: defineMessage({id: "call_list.detail", defaultMessage: "Detail"}),
                        },
                        renderer: detailRenderer,
                    },
                ]}
            />
        </div>
    );
};

export default IvrCalls;
